import { useState, useRef } from 'react'
import {
  useAdminExtractEvents,
  useAdminImportEvents,
  type ExtractedEventItem,
} from '../../hooks/useAdmin'

function formatDateShort(iso: string) {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })
  } catch { return iso }
}

const MAX_DIM = 2000

async function fileToBase64(file: File): Promise<{ data: string; mediaType: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const { naturalWidth: w, naturalHeight: h } = img
      const scale = Math.min(1, MAX_DIM / Math.max(w, h))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(w * scale)
      canvas.height = Math.round(h * scale)
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Image resize failed')); return }
          const reader = new FileReader()
          reader.onload = () => {
            const data = (reader.result as string).split(',')[1]
            resolve({ data, mediaType: 'image/jpeg' })
          }
          reader.onerror = () => reject(new Error('Could not read image'))
          reader.readAsDataURL(blob)
        },
        'image/jpeg',
        0.92,
      )
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not load image')) }
    img.src = url
  })
}

export function BulletinImportSection({
  churchId,
  churchName,
}: {
  churchId: string
  churchName: string
}) {
  const [mode, setMode] = useState<'images' | 'text'>('images')
  const [files, setFiles] = useState<File[]>([])
  const [text, setText] = useState('')
  const [extracted, setExtracted] = useState<ExtractedEventItem[] | null>(null)
  const [extractError, setExtractError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ imported: number; total: number; errors: string[] } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const extract = useAdminExtractEvents()
  const importEvents = useAdminImportEvents()

  async function handleExtract() {
    setExtracted(null)
    setExtractError(null)
    setImportResult(null)
    try {
      let result
      if (mode === 'images') {
        if (!files.length) return
        const images = await Promise.all(files.map(fileToBase64))
        result = await extract.mutateAsync({ churchId, images })
      } else {
        if (!text.trim()) return
        result = await extract.mutateAsync({ churchId, text })
      }
      const events = result.data.events
      if (!events.length) {
        setExtractError('No upcoming events found in the provided content.')
      } else {
        setExtracted(events)
      }
    } catch (err: unknown) {
      setExtractError(err instanceof Error ? err.message : 'Extraction failed')
    }
  }

  function handleImport() {
    if (!extracted) return
    setImportResult(null)
    importEvents.mutate(
      {
        churchId,
        events: extracted.map((ev) => ({
          title: ev.title,
          eventType: ev.eventType,
          description: ev.description ?? undefined,
          startsAt: ev.startsAt,
          endsAt: ev.endsAt ?? undefined,
        })),
      },
      {
        onSuccess: (res) => {
          setImportResult(res.data)
          setExtracted(null)
          setFiles([])
          setText('')
          if (fileInputRef.current) fileInputRef.current.value = ''
        },
      },
    )
  }

  const canExtract =
    !extract.isPending && (mode === 'images' ? files.length > 0 : text.trim().length > 0)

  const modeTab = (active: boolean): React.CSSProperties => ({
    padding: '5px 14px',
    borderRadius: 6,
    border: '1px solid var(--line)',
    background: active ? 'var(--surface-2)' : 'var(--surface)',
    fontFamily: 'var(--font-sans)',
    fontSize: 13,
    fontWeight: active ? 600 : 500,
    cursor: 'pointer',
    color: 'var(--ink)',
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Extract events from bulletin / post
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            style={modeTab(mode === 'images')}
            onClick={() => { setMode('images'); setExtracted(null); setExtractError(null) }}
          >
            Images
          </button>
          <button
            style={modeTab(mode === 'text')}
            onClick={() => { setMode('text'); setExtracted(null); setExtractError(null) }}
          >
            Paste text
          </button>
        </div>
      </div>

      {mode === 'images' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={(e) => {
              setFiles(Array.from(e.target.files ?? []))
              setExtracted(null)
              setExtractError(null)
            }}
            style={{ fontSize: 13, fontFamily: 'var(--font-sans)', color: 'var(--ink)' }}
          />
          {files.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              {files.length} file{files.length > 1 ? 's' : ''} selected — {files.map((f) => f.name).join(', ')}
            </div>
          )}
        </div>
      ) : (
        <textarea
          value={text}
          onChange={(e) => { setText(e.target.value); setExtracted(null); setExtractError(null) }}
          placeholder="Paste a social media post, newsletter text, or any announcement about upcoming events…"
          style={{
            width: '100%',
            minHeight: 120,
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid var(--line-soft)',
            background: 'var(--bg)',
            fontSize: 13,
            fontFamily: 'var(--font-sans)',
            color: 'var(--ink)',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      )}

      <button
        onClick={handleExtract}
        disabled={!canExtract}
        style={{
          padding: '10px 16px',
          borderRadius: 8,
          border: 'none',
          background: 'linear-gradient(135deg, #3B4CCA, #7C5CD3)',
          color: '#fff',
          fontSize: 13.5,
          fontFamily: 'var(--font-sans)',
          fontWeight: 600,
          cursor: !canExtract ? 'not-allowed' : 'pointer',
          opacity: !canExtract ? 0.55 : 1,
          alignSelf: 'flex-start',
        }}
      >
        {extract.isPending ? 'Extracting with Claude…' : '✦ Extract events with Claude'}
      </button>

      {extractError && (
        <div style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff5f5', fontSize: 13, color: '#b91c1c' }}>
          {extractError}
        </div>
      )}

      {extracted && extracted.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 500 }}>
            Found {extracted.length} event{extracted.length > 1 ? 's' : ''} — review before importing:
          </div>
          <div style={{ borderRadius: 10, border: '1px solid var(--line-soft)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--line-soft)' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)' }}>Title</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)' }}>Type</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>Starts</th>
                </tr>
              </thead>
              <tbody>
                {extracted.map((ev, i) => (
                  <tr key={i} style={{ borderTop: i > 0 ? '1px solid var(--line-soft)' : undefined }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500, color: 'var(--ink)' }}>{ev.title}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--muted)' }}>{ev.eventType}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{formatDateShort(ev.startsAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            onClick={handleImport}
            disabled={importEvents.isPending}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: '#22c55e',
              color: '#fff',
              fontSize: 13.5,
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              cursor: importEvents.isPending ? 'not-allowed' : 'pointer',
              opacity: importEvents.isPending ? 0.6 : 1,
              alignSelf: 'flex-start',
            }}
          >
            {importEvents.isPending
              ? 'Importing…'
              : `Import ${extracted.length} event${extracted.length > 1 ? 's' : ''} to ${churchName}`}
          </button>
        </div>
      )}

      {importResult && (
        <div style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', fontSize: 13, color: '#166534' }}>
          <div style={{ fontWeight: 600, marginBottom: importResult.errors.length ? 4 : 0 }}>
            ✓ Imported {importResult.imported} of {importResult.total} events
          </div>
          {importResult.errors.length > 0 && (
            <ul style={{ margin: '4px 0 0', paddingLeft: 16, color: '#b91c1c', fontSize: 12 }}>
              {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
